import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Calling } from 'react-iconly';
import { motion, useMotionValue, useSpring } from 'framer-motion';

const MagneticSticker = ({ sticker, mouseX, mouseY, index }) => {
  const ref = useRef(null);
  
  const xOffset = useSpring(0, { stiffness: 150, damping: 15, mass: 0.1 });
  const yOffset = useSpring(0, { stiffness: 150, damping: 15, mass: 0.1 });

  useEffect(() => {
    const updateMagnet = () => {
      if (!ref.current) return;
      const { left, top, width, height } = ref.current.getBoundingClientRect();
      const centerX = left + width / 2;
      const centerY = top + height / 2;
      
      const distanceX = mouseX.get() - centerX;
      const distanceY = mouseY.get() - centerY;
      const distance = Math.sqrt(distanceX ** 2 + distanceY ** 2);

      const radius = 250; // Trigger radius for magnetic effect

      if (distance < radius) {
        // Magnetic pull (strength 0.3)
        xOffset.set(distanceX * 0.3);
        yOffset.set(distanceY * 0.3);
      } else {
        xOffset.set(0);
        yOffset.set(0);
      }
    };

    const unsubscribeX = mouseX.on('change', updateMagnet);
    const unsubscribeY = mouseY.on('change', updateMagnet);

    return () => {
      unsubscribeX();
      unsubscribeY();
    };
  }, [mouseX, mouseY, xOffset, yOffset]);

  return (
    <motion.div
      ref={ref}
      initial={{ scale: 0, opacity: 0, rotate: 0 }}
      whileInView={{ scale: sticker.scale, opacity: 1, rotate: sticker.rotation }}
      transition={{ duration: 0.7, delay: index * 0.1, type: 'spring', bounce: 0.5 }}
      viewport={{ once: false, amount: 0.3 }}
      style={{
        position: 'absolute',
        top: sticker.top,
        left: sticker.left,
        width: 'clamp(60px, 12vw, 150px)',
        zIndex: 3,
        x: xOffset,
        y: yOffset
      }}
    >
      <img src={sticker.src} alt="" style={{ width: '100%', height: 'auto', filter: 'drop-shadow(0px 10px 20px rgba(0,0,0,0.3))' }} />
    </motion.div>
  );
};

const CTA = () => {
  const [randomizedStickers, setRandomizedStickers] = useState([]);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  useEffect(() => {
    const baseStickers = [
      '/sticker/footer-sticker-smiley.svg',
      '/sticker/footer-sticker-heart.svg',
      '/sticker/footer-sticker-hands.svg',
      '/sticker/footer-sticker-100.svg',
      '/sticker/footer-sticker-camera.svg',
      '/sticker/footer-sticker-boom.svg',
    ];
    
    // Spread them across 6 zones to avoid overlaps and stay out of the text
    const zones = [
      { top: [5, 20], left: [5, 25] }, // Top Left
      { top: [5, 20], left: [75, 90] }, // Top Right
      { top: [40, 60], left: [5, 15] }, // Middle Left
      { top: [40, 60], left: [80, 90] }, // Middle Right
      { top: [75, 85], left: [15, 30] }, // Bottom Left
      { top: [75, 85], left: [70, 85] }  // Bottom Right
    ];

    // Shuffle zones
    const shuffledZones = [...zones].sort(() => Math.random() - 0.5);

    const generated = baseStickers.map((src, i) => {
      const zone = shuffledZones[i];
      const randomTop = Math.random() * (zone.top[1] - zone.top[0]) + zone.top[0];
      const randomLeft = Math.random() * (zone.left[1] - zone.left[0]) + zone.left[0];
      const randomRotation = (Math.random() - 0.5) * 60; // -30 to 30 deg
      const randomScale = 0.8 + Math.random() * 0.4; // 0.8 to 1.2
      return {
        id: i,
        src,
        top: `${randomTop}%`,
        left: `${randomLeft}%`,
        rotation: randomRotation,
        scale: randomScale
      };
    });

    setRandomizedStickers(generated);
  }, []);

  const handleMouseMove = (e) => {
    mouseX.set(e.clientX);
    mouseY.set(e.clientY);
  };

  return (
    <section 
      className="dark-section" 
      onMouseMove={handleMouseMove}
      style={{ 
        backgroundColor: '#1A1816',
        color: 'white', 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '160px 24px',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Social Links top center */}
      <div style={{ position: 'absolute', top: '40px', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', maxWidth: '600px' }}>
        {[
          {
            url: 'https://instagram.com/alvinahmad',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
          },
          {
            url: 'https://x.com/alvinahmad',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          },
          {
            url: 'https://linkedin.com/in/alvinahmad',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
          },
          {
            url: 'https://behance.net/alvinahmad',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22 7h-7v-2h7v2zm1.726 10c-.442 1.297-2.029 3-5.101 3-3.074 0-5.564-1.729-5.564-5.675 0-3.91 2.325-5.92 5.466-5.92 3.082 0 4.964 1.782 5.375 4.426.078.506.109 1.188.095 2.14h-8.027c.13 3.211 3.483 3.312 4.588 2.029h3.168zm-7.686-4h4.965c-.105-1.547-1.136-2.219-2.477-2.219-1.466 0-2.277.768-2.488 2.219zm-9.574 6.988H0V5.021h6.953c5.476.081 5.58 5.444 2.72 6.906 3.461 1.26 3.577 8.061-3.207 8.061zM3 11h3.584c2.508 0 2.906-3-.312-3H3v3zm3.391 3H3v3.016h3.341c3.055 0 2.868-3.016.05-3.016z"/></svg>
          },
          {
            url: 'https://facebook.com/alvinahmad',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          },
          {
            url: 'https://tiktok.com/@alvinahmad',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.92a8.16 8.16 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.35z"/></svg>
          },
          {
            url: 'https://threads.net/@alvinahmad',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32L7.734 7.847c.98-1.454 2.568-2.256 4.478-2.256h.044c3.194.02 5.097 1.975 5.287 5.388.108.046.216.094.321.142 1.49.7 2.58 1.761 3.154 3.07.797 1.82.871 4.79-1.548 7.158-1.85 1.81-4.094 2.628-7.277 2.65Zm1.003-11.69c-.242 0-.487.007-.739.021-1.836.103-2.98.946-2.916 2.143.067 1.256 1.452 1.839 2.784 1.767 1.224-.065 2.818-.543 3.086-3.71a10.5 10.5 0 0 0-2.215-.221z"/></svg>
          },
          {
            url: 'https://youtube.com/@alvinahmad',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
          },
          {
            url: 'https://fiverr.com/alvinahmad',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23.004 15.588a.995.995 0 1 0 .002-1.99.995.995 0 0 0-.002 1.99zm-.996-3.705h-.85c-.546 0-.84.41-.84 1.092v2.466h-1.61v-3.558h-.684c-.547 0-.84.41-.84 1.092v2.466h-1.61v-4.874h1.61v.74c.264-.574.626-.74 1.163-.74h1.972v.74c.264-.574.625-.74 1.162-.74h.527v1.316zm-6.786 1.501h-3.359c.088.546.43.858 1.006.858.43 0 .732-.175.83-.487l1.425.4c-.351.848-1.22 1.364-2.255 1.364-1.748 0-2.549-1.355-2.549-2.515 0-1.14.703-2.505 2.45-2.505 1.856 0 2.471 1.384 2.471 2.408 0 .224-.01.37-.02.477zm-1.562-.945c-.04-.42-.342-.81-.889-.81-.508 0-.81.225-.908.81h1.797zM7.508 15.44h1.416l1.767-4.874h-1.62l-.86 2.837-.878-2.837H5.72l1.787 4.874zm-6.6 0H2.51v-3.558h1.524v3.558h1.591v-4.874H2.51v-.302c0-.332.235-.536.606-.536h.918V8.412H2.85c-1.162 0-1.943.712-1.943 1.755v.4H0v1.316h.908v3.558z"/></svg>
          },
          {
            url: 'https://upwork.com/freelancers/alvinahmad',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.561 13.158c-1.102 0-2.135-.467-3.074-1.227l.228-1.076.008-.042c.207-1.143.849-3.06 2.839-3.06 1.492 0 2.703 1.212 2.703 2.703-.001 1.489-1.212 2.702-2.704 2.702zm0-8.14c-2.539 0-4.51 1.649-5.31 4.366-1.22-1.834-2.148-4.036-2.687-5.892H7.828v7.112c-.002 1.406-1.141 2.546-2.547 2.548-1.405-.002-2.543-1.143-2.545-2.548V3.492H0v7.112c0 2.914 2.37 5.303 5.281 5.303 2.913 0 5.283-2.389 5.283-5.303v-1.19c.529 1.107 1.182 2.229 1.974 3.221l-1.673 7.873h2.797l1.213-5.71c1.063.679 2.285 1.109 3.686 1.109 3 0 5.439-2.452 5.439-5.45 0-3-2.439-5.439-5.439-5.439z"/></svg>
          },
          {
            url: 'https://dribbble.com/alvinahmad',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 24C5.385 24 0 18.615 0 12S5.385 0 12 0s12 5.385 12 12-5.385 12-12 12zm10.12-10.358c-.35-.11-3.17-.953-6.384-.438 1.34 3.684 1.887 6.684 1.992 7.308 2.3-1.555 3.936-4.02 4.395-6.87zm-6.115 7.808c-.153-.9-.75-4.032-2.19-7.77l-.066.02c-5.79 2.015-7.86 6.025-8.04 6.4 1.73 1.358 3.92 2.166 6.29 2.166 1.42 0 2.77-.29 4-.814zm-11.62-2.58c.232-.4 3.045-5.055 8.332-6.765.135-.045.27-.084.405-.12-.26-.585-.54-1.167-.832-1.74C7.17 11.775 2.206 11.71 1.756 11.7l-.004.312c0 2.633.998 5.037 2.634 6.855zm-2.42-8.955c.46.008 4.683.026 9.477-1.248-1.698-3.018-3.53-5.558-3.8-5.928-2.868 1.35-5.01 3.99-5.676 7.17zM9.6 2.052c.282.38 2.145 2.914 3.822 6 3.645-1.365 5.19-3.44 5.373-3.702-1.81-1.61-4.19-2.586-6.795-2.586-.825 0-1.63.1-2.4.285zm10.335 3.483c-.218.29-1.935 2.493-5.724 4.04.24.49.47.985.68 1.486.08.18.15.36.22.53 3.41-.43 6.8.26 7.14.33-.02-2.42-.88-4.64-2.31-6.38z"/></svg>
          },
          {
            url: 'https://github.com/alvinahmad',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
          }
        ].map((social, i) => (
          <a key={i} href={social.url} target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '6px',
            width: '40px',
            height: '40px',
            color: 'white',
            textDecoration: 'none',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            {social.icon}
          </a>
        ))}
      </div>

      {/* Main Text Content */}
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
        <h1 style={{ fontSize: 'clamp(64px, 12vw, 160px)', fontFamily: "'Youth', sans-serif", fontWeight: 900, lineHeight: 0.9, letterSpacing: '-0.04em', margin: 0, position: 'relative', zIndex: 4, pointerEvents: 'none' }}>
          Let's <span style={{ color: '#FF7722' }}>unlock</span> the<br />
          door <span style={{ color: '#FF7722' }}>to your</span><br />
          success
        </h1>
        
        {/* Floating Stickers */}
        {randomizedStickers.map((sticker, i) => (
          <MagneticSticker 
            key={sticker.id} 
            sticker={sticker} 
            mouseX={mouseX} 
            mouseY={mouseY} 
            index={i} 
          />
        ))}
      </div>

      {/* Quote and Call button */}
      <div style={{ marginTop: '64px', display: 'flex', alignItems: 'center', gap: '16px', zIndex: 5, position: 'relative' }}>
        <span style={{ color: 'white', fontSize: '11px', fontFamily: 'Youth, sans-serif', fontWeight: 900, lineHeight: '20px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>GET YOUR QUOTE IN 24H</span>
        <a href="mailto:alvin@alvinahmad.dev" style={{ 
          backgroundColor: 'white', 
          border: 'none', 
          borderRadius: '50%', 
          width: '40px', 
          height: '40px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          cursor: 'pointer',
          textDecoration: 'none'
        }}>
          <Calling size={20} color="#1A1816" set="curved" weight="bold" />
        </a>
      </div>

      {/* Bottom Footer Elements */}
      <div style={{ position: 'absolute', bottom: '40px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', padding: '0 40px', boxSizing: 'border-box', flexWrap: 'wrap', zIndex: 5 }}>
         <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
           ©2026 Alvin Ahmad
         </div>
         <Link to="/privacy" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Privacy</Link>
         <Link to="/terms" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Terms</Link>
      </div>
    </section>
  );
};

export default CTA;
